/**
 * Centralized data hooks — the ONLY way features should talk to the
 * query layer going forward.
 *
 *   useApiQuery(key, path, opts)       GET with SWR caching
 *   useApiMutation(method, path, opts) POST/PATCH/DELETE + invalidation
 *   useGlobalLoading()                 app-wide "anything in flight?" flag
 *   useQueryErrorText(error)           consistent Arabic error strings
 *
 * Offline persistence is opt-in per query:
 *   useApiQuery(['profile','me'], '/auth/me', { persist: true })
 */
import { useCallback } from 'react';
import {
  useQuery, useMutation, useQueryClient, useIsFetching, useIsMutating,
} from '@tanstack/react-query';
import { apiGet, apiSend } from '../lib/query/apiClient';
import { toErrorText } from '../lib/toErrorText';

/**
 * @param {Array}  queryKey  stable key, e.g. ['name-change-requests','my']
 * @param {string} path      backend path WITHOUT the /api prefix
 * @param {object} options   TanStack options + { persist, silent }
 */
export function useApiQuery(queryKey, path, options = {}) {
  const { persist = false, silent = false, meta, ...rest } = options;
  return useQuery({
    queryKey,
    queryFn: ({ signal }) => apiGet(path, { signal }),
    meta: { ...(meta || {}), persist, silent },
    ...rest,
  });
}

/**
 * @param {string} method       'post' | 'patch' | 'put' | 'delete'
 * @param {string|function} path  path or (variables) => path
 * @param {object} options      TanStack options + { invalidates: [keys], silent }
 */
export function useApiMutation(method, path, options = {}) {
  const { invalidates = [], silent = false, meta, onSuccess, ...rest } = options;
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (variables) => {
      const url = typeof path === 'function' ? path(variables) : path;
      return apiSend(method, url, variables);
    },
    meta: { ...(meta || {}), silent },
    onSuccess: async (data, variables, context) => {
      await Promise.all(
        invalidates.map((key) => qc.invalidateQueries({ queryKey: key })),
      );
      if (onSuccess) await onSuccess(data, variables, context);
    },
    ...rest,
  });
}

/** True while ANY query or mutation is in flight — powers a global spinner/bar. */
export function useGlobalLoading() {
  const fetching = useIsFetching();
  const mutating = useIsMutating();
  return fetching + mutating > 0;
}

/** Stable helper returning a renderable Arabic error string. */
export function useQueryErrorText() {
  return useCallback((error, fallback) => toErrorText(error, fallback), []);
}
